import * as React from 'react';
import cx from 'classnames';

import { disableScroll, enableScroll } from '@common/utils/scroll';

import './hint.styles.scss';

export type HintProps = React.PropsWithChildren<{
	className?: string;
	description: React.ReactNode;
}>;

export const Hint: React.FC<HintProps> = (props) => {
	const { children = '?', className, description } = props;

	const [opened, setOpened] = React.useState(false);
	const [clicked, setClicked] = React.useState(false);
	const [cords, setCoords] = React.useState<{ top: number; left: number } | undefined>();

	const descriptionRef = React.useRef<HTMLDivElement>();
	const hintRef = React.useRef<HTMLSpanElement>(null);
	const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

	const open = React.useCallback(() => {
		setOpened(true);
	}, []);

	const handleOpen = React.useCallback((e: React.MouseEvent) => {
		e.persist();
		e.stopPropagation();

		setClicked(true);
		open();
		setCoords({
			top: e.clientY,
			left: e.clientX,
		});
		disableScroll();
	}, [open]);

	const handleMouseEnter = React.useCallback(() => {
		if (description && !clicked && hintRef.current) {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
			const rect = hintRef.current.getBoundingClientRect();
			open();
			setCoords({
				top: rect.top + rect.height / 2,
				left: rect.right + 8,
			});
		}
	}, [description, open, clicked]);

	const handleMouseLeave = React.useCallback(() => {
		if (!clicked) {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
			hoverTimeoutRef.current = setTimeout(() => {
				setOpened(false);
			}, 100);
		}
	}, [clicked]);

	const handleDescriptionMouseEnter = React.useCallback(() => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
		}
		if (!clicked) {
			setOpened(true);
		}
	}, [clicked]);

	const handleDescriptionMouseLeave = React.useCallback(() => {
		if (!clicked) {
			setOpened(false);
		}
	}, [clicked]);

	const close = React.useCallback(() => {
		setOpened(false);
		setClicked(false);
		enableScroll();
	}, []);

	React.useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	// Update tooltip position on hover
	React.useEffect(() => {
		if (opened && !clicked && hintRef.current && descriptionRef.current) {
			const hintRect = hintRef.current.getBoundingClientRect();
			const tooltipHeight = descriptionRef.current.offsetHeight;
			
			setCoords({
				top: hintRect.top + hintRect.height / 2 - tooltipHeight / 2,
				left: hintRect.right + 8,
			});
		}
	}, [opened, clicked]);

	return (
		<>
			{opened && clicked && <div className="Hint__backdrop" onClick={close} />}
			{description && (
				<div 
					ref={descriptionRef as unknown as React.LegacyRef<HTMLDivElement>} 
					className={cx('Hint__description', { 'Hint__description__opened': opened })}
					style={{
						top: cords && descriptionRef.current 
							? (clicked ? cords.top - descriptionRef.current.clientHeight - 30 : cords.top)
							: undefined,
						left: cords?.left,
					}}
					onMouseEnter={handleDescriptionMouseEnter}
					onMouseLeave={handleDescriptionMouseLeave}
				>
					{description}
				</div>
			)}
			<span 
				ref={hintRef}
				className={cx('Hint', { 'Hint--disabled': !description }, className)} 
				onClick={description ? handleOpen : undefined}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{children}
			</span>
		</>
	);
};